import type { StrokePoints } from "../handwriting/stroke-shape.js";
import { taughtStrokes } from "../handwriting/taught-strokes.js";
import { DrillMarking } from "./xiezi-marking.js";
import type {
  DrillCharacter,
  XieziDrillProperties,
} from "./xiezi-drill-properties.js";
import { DrillFeedback } from "./xiezi-feedback.js";
import { checkLanded } from "./xiezi-landed.js";
import { strokeLine } from "./xiezi-copy.js";
import type { NamedCharacter } from "./xiezi-stage-words.js";
import type { MarkedPass } from "./xiezi-marked.js";
import {
  type DrillProgress,
  drillStart,
  inkStays,
  passCleared,
  strokePlaced,
  strokeTakenBack,
} from "../handwriting/xiezi-stages.js";
import { type DrillParts, showNote, showProgress } from "./xiezi-view.js";

/**
 * The 汉字 writing drill: one character in one square.
 *
 * A reader meeting a character for the first time traces it, then writes it
 * with the strokes snapping into place, then writes it from memory, each
 * stage five times, with the help falling away a piece at a time (#962).
 * `src/cidian-page/cidian-xiezi-markup.ts` draws the block under a 词典
 * entry's stroke figure, `xiezi-start.ts` builds this on the first open, and
 * this is the drill itself. Nothing is kept. A reader who closes the page
 * starts again.
 *
 * **Each stroke is marked as it lands, in the first three stages.** A stroke
 * taken snaps into place, and any other is named and cleared for another go.
 * The fourth stage is marked whole, by whichever {@link CharacterMarker} the
 * drill was given. `xiezi-check.ts` does both markings on the public page,
 * `src/handwriting/xiezi-stages.ts` is where the reader is, and
 * `xiezi-view.ts` puts that on the page.
 *
 * **The fourth stage checks itself once the strokes are all there.** Ink with
 * as many strokes as the character is marked without a press, and a pass is
 * taken as though Check had been pressed. A miss is let go without a word, so
 * a reader who wrote it wrong and has not noticed presses Check to hear why.
 * Pressing Check for every pass broke the run of writing it five times.
 *
 * **A finished drill takes no ink.** The square is locked until Again, or
 * until an Exercise puts another character in it. `xiezi-view.ts` locks it.
 *
 * **An Exercise moves the drill between characters.** `lianxi-xiezi.ts` holds
 * several characters and each one's place, hears that a pass has ended through
 * `onPass`, and puts the next character in the square with {@link show}. The
 * drill itself knows one character at a time.
 *
 * **Each moment is heard as well as seen.** A stroke taken, a stroke missed,
 * the character written whole, a stage done and the drill done each play a
 * note through the {@link DrillSounds} the drill was given, and a character
 * written whole is said aloud after its note and flashes green in the square.
 * A stroke missed in a stage that snaps is written in for the reader to see.
 * `xiezi-sounds.ts` is the player and `xiezi-square.ts` the square.
 */

/** One drill, on one block, over one character at a time. */
export class XieziDrill {
  readonly #parts: DrillParts;
  readonly #marking: DrillMarking;
  readonly #onPass: ((progress: DrillProgress) => void) | undefined;
  readonly #feedback: DrillFeedback;
  #named: NamedCharacter = { character: "" };
  #taught: readonly StrokePoints[] = [];
  #progress: DrillProgress = drillStart();

  constructor(properties: XieziDrillProperties) {
    this.#parts = properties.parts;
    this.#marking = new DrillMarking({
      ...properties,
      apply: this.#applyMark.bind(this),
    });
    this.#onPass = properties.onPass;
    this.#feedback = new DrillFeedback(properties);
    this.show({ ...properties, progress: drillStart() });
  }

  /**
   * Put a character in the square, at where the reader is with it.
   *
   * The square clears the ink, since it was the last character's.
   * @param held - The character, its outlines and the reader's place.
   */
  show(held: DrillCharacter): void {
    this.#named = held;
    this.#taught = taughtStrokes(held.strokes.medians);
    this.#feedback.prepare(held.recordings ?? []);
    this.#parts.square.show(held.strokes);
    this.#moved(held.progress);
  }

  /** A stroke has landed on the pad. */
  strokeLanded(): void {
    this.#marking.whenWhole();

    const { square } = this.#parts;
    const next = this.#progress.stroke;
    const verdict = checkLanded(square, this.#taught, this.#progress);

    if (verdict?.kind === "right") {
      this.#strokeTaken(strokePlaced(this.#progress, square.strokeCount));
    } else if (verdict !== undefined) {
      showNote(this.#parts, strokeLine(verdict, next), "wrong");
      this.#feedback.strokeMissed(next);
    }
  }

  /** The last stroke taken back. */
  undo(): void {
    if (inkStays(this.#progress)) {
      this.#parts.square.dropLastInk();
    } else {
      this.#moved(strokeTakenBack(this.#progress));
    }
  }

  /** The pass started again. */
  clear(): void {
    this.#parts.square.clearInk();
    this.#moved(passCleared(this.#progress));
  }

  /**
   * The character written from memory, marked whole, on a press of Check.
   * `xiezi-marking.ts` has how a press and the automatic check share it.
   */
  async check(): Promise<void> {
    await this.#marking.pressed();
  }

  /** Move the reader by a mark on the character written from memory. */
  #applyMark(pass: MarkedPass): void {
    const { after } = pass;

    if (after !== undefined) {
      this.#parts.square.clearInk();

      if (pass.passed) {
        this.#passDone(after);
      } else {
        this.#moved(after);
        this.#feedback.passMissed();
        this.#onPass?.(after);
      }
    }

    showNote(this.#parts, pass.line, pass.verdict);
  }

  /** Take the reader to `progress`, and show it. */
  #moved(progress: DrillProgress, done?: "pass" | "stage"): void {
    this.#progress = progress;
    this.#marking.moved(this.#named, progress);
    showProgress(this.#parts, progress, this.#named, done);
  }

  /** A stroke taken, which may have been the last of the pass. */
  #strokeTaken(progress: DrillProgress): void {
    if (progress.stroke === 0) {
      this.#passDone(progress);
    } else {
      this.#moved(progress);
      this.#feedback.strokeTaken();
    }
  }

  /**
   * A pass done, the character written whole.
   *
   * It flashes, it is said, and the note for what the pass finished plays
   * under it. Whether the drill is finished is asked before the Exercise is
   * told about the pass, since telling it moves the square on.
   */
  #passDone(progress: DrillProgress): void {
    const before = this.#progress;

    this.#moved(progress, progress.stage > before.stage ? "stage" : "pass");
    this.#feedback.passDone(before, progress);
    this.#onPass?.(progress);
  }
}
